import React, { useState, useEffect } from 'react';
import * as Icons from 'lucide-react';
import api from '../../services/api';

const cardStyle = { border: '1px solid rgba(99, 102, 241, 0.08)', boxShadow: '0 1px 3px rgba(99, 102, 241, 0.06)' };

const GlobalAPIManagement = () => {
  const [activeService, setActiveService] = useState('all');
  const [apiBaseUrl, setApiBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState('');
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [configRes, profileRes] = await Promise.all([
          api.get('/user/api-config'),
          api.get('/user/settings/profile'),
        ]);
        if (configRes.data?.api_base_url) setApiBaseUrl(configRes.data.api_base_url);
        if (profileRes.data?.api_key) setApiKey(profileRes.data.api_key);
      } catch (err) {
        console.error('Failed to fetch API config:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const handleRegenerate = async () => {
    if (regenerating) return;
    if (!window.confirm('Are you sure you want to regenerate your API key? The old key will stop working immediately.')) return;
    setRegenerating(true);
    try {
      const res = await api.post('/user/settings/regenerate-api-key', { length: 32 });
      if (res.data?.api_key) {
        setApiKey(res.data.api_key);
        setShowKey(true);
      }
    } catch (err) {
      console.error('Failed to regenerate API key:', err);
    } finally {
      setRegenerating(false);
    }
  };

  const CopyBtn = ({ text, label, size = 'sm' }) => (
    <button
      onClick={(e) => { e.stopPropagation(); copyToClipboard(text, label); }}
      className={`flex items-center gap-1 rounded-lg font-semibold transition-all hover:shadow-sm ${size === 'sm' ? 'px-2 py-1 text-[10px]' : 'px-3 py-1.5 text-xs'}`}
      style={copied === label
        ? { background: 'rgba(16, 185, 129, 0.1)', color: '#059669', border: '1px solid rgba(16, 185, 129, 0.2)' }
        : { background: 'rgba(99, 102, 241, 0.06)', color: '#6366f1', border: '1px solid rgba(99, 102, 241, 0.12)' }
      }
    >
      {copied === label ? <><Icons.CheckCircle className="w-3 h-3" /> Copied!</> : <><Icons.Copy className="w-3 h-3" /> Copy</>}
    </button>
  );

  const maskedKey = apiKey ? apiKey.substring(0, 6) + '••••••••' + apiKey.substring(apiKey.length - 4) : '••••••••••••';
  const displayKey = showKey ? apiKey : maskedKey;

  const serviceColors = {
    messaging: { bg: 'rgba(99, 102, 241, 0.08)', text: '#4f46e5', label: 'Messaging', icon: 'MessageSquare' },
    whatsapp: { bg: 'rgba(37, 211, 102, 0.08)', text: '#25D366', label: 'WhatsApp', icon: 'MessageCircle' },
    rcs: { bg: 'rgba(66, 133, 244, 0.08)', text: '#4285F4', label: 'Google RCS', icon: 'Smartphone' },
    email: { bg: 'rgba(245, 158, 11, 0.08)', text: '#F59E0B', label: 'Bulk Email', icon: 'Mail' },
  };

  const endpoints = [
    { method: 'POST', path: '/api/sendsms', service: 'messaging', desc: 'Send single SMS via HTTP POST with query/form parameters', auth: 'API Key (key param)', status: 'active' },
    { method: 'GET', path: '/api/sendsms', service: 'messaging', desc: 'Send single SMS via HTTP GET with query parameters', auth: 'API Key (key param)', status: 'active' },
    { method: 'POST', path: '/api/json/sendsms/', service: 'messaging', desc: 'Send bulk SMS via JSON body (supports multiple recipients)', auth: 'API Key (key header)', status: 'active' },
    { method: 'POST', path: '/api/wa/send', service: 'whatsapp', desc: 'Send WhatsApp message', auth: 'API Key', status: 'coming_soon' },
    { method: 'POST', path: '/api/rcs/send', service: 'rcs', desc: 'Send RCS message', auth: 'API Key', status: 'coming_soon' },
    { method: 'POST', path: '/api/email/send', service: 'email', desc: 'Send bulk email', auth: 'API Key', status: 'coming_soon' },
  ];

  const methodColors = {
    GET: { bg: '#dcfce7', text: '#166534', border: '#bbf7d0' },
    POST: { bg: '#dbeafe', text: '#1e40af', border: '#bfdbfe' },
  };

  const filteredEndpoints = activeService === 'all' ? endpoints : endpoints.filter(e => e.service === activeService);

  // HTTP API example URL
  const httpExampleUrl = `${apiBaseUrl}/api/sendsms?key=YOUR_API_KEY&mobiles=+919999999999&sms=Hello%20World&senderid=ABCDEF&entityid=1234567890123456789&tempid=1234567890123456`;

  // JSON API example body
  const jsonExampleBody = JSON.stringify({
    listsms: [{
      sms: "Your OTP is 123456. Valid for 5 minutes.",
      mobiles: "+919999999999",
      senderid: "ABCDEF",
      entityid: "1234567890123456789",
      tempid: "1234567890123456"
    }]
  }, null, 2);

  // HTTP API params
  const httpParams = [
    { name: 'key', type: 'string', desc: 'Your API authentication key', required: true },
    { name: 'mobiles', type: 'string', desc: 'Recipient mobile number with country code (e.g. +919999999999)', required: true },
    { name: 'sms', type: 'string', desc: 'Message content (URL encoded for GET requests)', required: true },
    { name: 'senderid', type: 'string', desc: 'DLT approved sender ID / header (6 characters)', required: true },
    { name: 'entityid', type: 'string', desc: 'DLT registered principal entity ID', required: true },
    { name: 'tempid', type: 'string', desc: 'DLT registered content template ID', required: true },
    { name: 'unicode', type: 'boolean', desc: 'Set to true for Unicode/multilingual messages (auto-detected if omitted)', required: false },
  ];

  // JSON API fields
  const jsonFields = [
    { name: 'sms', type: 'string', desc: 'Message content (DLT approved template text)', required: true },
    { name: 'mobiles', type: 'string', desc: 'Recipient mobile with country code (+91...)', required: true },
    { name: 'senderid', type: 'string', desc: 'DLT approved sender ID / header (6 chars)', required: true },
    { name: 'entityid', type: 'string', desc: 'DLT registered principal entity ID', required: true },
    { name: 'tempid', type: 'string', desc: 'DLT registered content template ID', required: true },
  ];

  const successResponse = JSON.stringify({
    smslist: {
      sms: { reason: "success", code: "000", messageid: "709462811", mobileno: "+919999999999", status: "success" }
    }
  }, null, 2);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <Icons.Code2 className="w-5 h-5 text-white" />
            </div>
            API Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage API keys and endpoints across all services</p>
        </div>
      </div>

      {/* Credentials Card */}
      <div className="bg-white rounded-xl overflow-hidden" style={cardStyle}>
        <div className="px-5 py-3.5 flex items-center gap-2" style={{ background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.06), rgba(6, 182, 212, 0.04))', borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
          <Icons.Lock className="w-4 h-4 text-indigo-500" />
          <h3 className="text-sm font-bold text-gray-800">Your API Credentials</h3>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Base URL */}
            <div className="rounded-xl p-4" style={{ background: 'rgba(99, 102, 241, 0.03)', border: '1px solid rgba(99, 102, 241, 0.1)' }}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Icons.Server className="w-3.5 h-3.5 text-indigo-500" />
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Base URL</span>
                </div>
                <CopyBtn text={apiBaseUrl} label="baseurl" />
              </div>
              <p className="text-sm font-mono font-semibold text-gray-800 break-all">{apiBaseUrl || 'Not configured'}</p>
            </div>
            {/* API Key */}
            <div className="rounded-xl p-4" style={{ background: 'rgba(245, 158, 11, 0.03)', border: '1px solid rgba(245, 158, 11, 0.1)' }}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Icons.Key className="w-3.5 h-3.5 text-amber-500" />
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">API Key</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setShowKey(!showKey)}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold transition-all hover:shadow-sm"
                    style={{ background: 'rgba(99, 102, 241, 0.06)', color: '#6366f1', border: '1px solid rgba(99, 102, 241, 0.12)' }}>
                    {showKey ? <Icons.EyeOff className="w-3 h-3" /> : <Icons.Eye className="w-3 h-3" />}
                    {showKey ? 'Hide' : 'Show'}
                  </button>
                  <CopyBtn text={apiKey} label="apikey" />
                </div>
              </div>
              <p className="text-sm font-mono font-semibold text-gray-800 break-all">{displayKey}</p>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between gap-3">
            <div className="flex items-start gap-2 p-3 rounded-lg flex-1" style={{ background: 'rgba(245, 158, 11, 0.05)', border: '1px solid rgba(245, 158, 11, 0.1)' }}>
              <Icons.AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-amber-700">Keep your API key secure. Never expose it in client-side code or public repositories.</p>
            </div>
            <button
              onClick={handleRegenerate}
              disabled={regenerating}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white font-medium text-sm flex-shrink-0 transition-all hover:shadow-lg disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}
            >
              <Icons.RefreshCw className={`w-4 h-4 ${regenerating ? 'animate-spin' : ''}`} />
              {regenerating ? 'Regenerating...' : 'Regenerate Key'}
            </button>
          </div>
        </div>
      </div>

      {/* Service Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { key: 'all', label: 'All Services', color: '#6366f1' },
          { key: 'messaging', label: 'Messaging', color: '#4f46e5' },
          { key: 'whatsapp', label: 'WhatsApp', color: '#25D366' },
          { key: 'rcs', label: 'Google RCS', color: '#4285F4' },
          { key: 'email', label: 'Bulk Email', color: '#F59E0B' },
        ].map((svc) => (
          <button
            key={svc.key}
            onClick={() => setActiveService(svc.key)}
            className="px-4 py-2 rounded-xl text-sm font-medium transition-all"
            style={{
              background: activeService === svc.key ? `${svc.color}14` : 'white',
              color: activeService === svc.key ? svc.color : '#6b7280',
              border: `1px solid ${activeService === svc.key ? `${svc.color}33` : '#e5e7eb'}`,
            }}
          >
            {svc.label}
          </button>
        ))}
      </div>

      {/* API Endpoints Reference */}
      <div className="bg-white rounded-xl overflow-hidden" style={cardStyle}>
        <div className="px-5 py-3.5 flex items-center gap-2" style={{ background: 'rgba(99, 102, 241, 0.04)', borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
          <Icons.Globe className="w-4 h-4 text-indigo-500" />
          <h3 className="text-sm font-bold text-gray-800">API Endpoints</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
                <th className="text-left px-5 py-2.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Method</th>
                <th className="text-left px-5 py-2.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Endpoint</th>
                <th className="text-left px-5 py-2.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Service</th>
                <th className="text-left px-5 py-2.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Description</th>
                <th className="text-center px-5 py-2.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredEndpoints.map((ep, i) => (
                <tr key={i} className="hover:bg-gray-50/50" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.05)' }}>
                  <td className="px-5 py-3">
                    <span className="px-2 py-0.5 rounded-md text-xs font-bold" style={{
                      background: methodColors[ep.method]?.bg,
                      color: methodColors[ep.method]?.text,
                      border: `1px solid ${methodColors[ep.method]?.border}`,
                    }}>
                      {ep.method}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <code className="text-xs text-gray-800 font-mono font-semibold">{ep.path}</code>
                  </td>
                  <td className="px-5 py-3">
                    <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold" style={{
                      background: serviceColors[ep.service]?.bg,
                      color: serviceColors[ep.service]?.text,
                    }}>
                      {serviceColors[ep.service]?.label}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-xs text-gray-600">{ep.desc}</td>
                  <td className="px-5 py-3 text-center">
                    {ep.status === 'active' ? (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-50 text-green-600 border border-green-100">Active</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-600 border border-amber-100">Coming Soon</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Messaging API Details */}
      {(activeService === 'all' || activeService === 'messaging') && (
        <>
          {/* HTTP API Section */}
          <div className="bg-white rounded-xl overflow-hidden" style={cardStyle}>
            <div className="px-5 py-3.5 flex items-center gap-2" style={{ background: 'linear-gradient(135deg, rgba(22, 163, 74, 0.06), rgba(6, 182, 212, 0.04))', borderBottom: '1px solid rgba(22, 163, 74, 0.08)' }}>
              <Icons.Link2 className="w-4 h-4 text-green-600" />
              <h3 className="text-sm font-bold text-gray-800">HTTP API — Send Single SMS</h3>
              <span className="ml-auto px-2 py-0.5 rounded-lg text-[10px] font-bold text-white" style={{ background: 'linear-gradient(135deg, #059669, #06b6d4)' }}>GET / POST</span>
            </div>
            <div className="p-5 space-y-4">
              {/* Endpoint URL */}
              <div>
                <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Endpoint</h4>
                <div className="flex items-center gap-2 rounded-xl p-3" style={{ background: '#1e1e2e', border: '1px solid rgba(99, 102, 241, 0.15)' }}>
                  <code className="text-xs font-mono text-emerald-400 flex-1 break-all">{apiBaseUrl}/api/sendsms?key=...&mobiles=...&sms=...</code>
                  <CopyBtn text={httpExampleUrl} label="httpurl" />
                </div>
              </div>

              {/* Parameters */}
              <div>
                <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Parameters</h4>
                <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(99, 102, 241, 0.08)' }}>
                  <table className="min-w-full">
                    <thead><tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Parameter</th>
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Required</th>
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Description</th>
                    </tr></thead>
                    <tbody>
                      {httpParams.map((p) => (
                        <tr key={p.name} style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.05)' }}>
                          <td className="px-4 py-2.5 text-xs font-semibold text-indigo-600 font-mono">{p.name}</td>
                          <td className="px-4 py-2.5"><span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: 'rgba(99, 102, 241, 0.08)', color: '#4f46e5' }}>{p.type}</span></td>
                          <td className="px-4 py-2.5"><span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${p.required ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-gray-50 text-gray-500 border border-gray-200'}`}>{p.required ? 'Yes' : 'No'}</span></td>
                          <td className="px-4 py-2.5 text-xs text-gray-600">{p.desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Example */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Example Request (cURL)</h4>
                  <CopyBtn text={`curl -X POST "${apiBaseUrl}/api/sendsms" -d "key=${apiKey || 'YOUR_API_KEY'}" -d "mobiles=+919999999999" -d "sms=Hello World" -d "senderid=ABCDEF" -d "entityid=1234567890123456789" -d "tempid=1234567890123456"`} label="httpcurl" />
                </div>
                <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(99, 102, 241, 0.15)' }}>
                  <div className="flex items-center px-4 py-2" style={{ background: '#252535' }}>
                    <span className="text-[10px] font-mono text-amber-400">cURL</span>
                  </div>
                  <div className="p-4 overflow-x-auto" style={{ background: '#1e1e2e' }}>
                    <pre className="text-xs font-mono text-amber-400 whitespace-pre-wrap">{`curl -X POST "${apiBaseUrl}/api/sendsms" \\
  -d "key=${showKey ? apiKey : 'YOUR_API_KEY'}" \\
  -d "mobiles=+919999999999" \\
  -d "sms=Hello World" \\
  -d "senderid=ABCDEF" \\
  -d "entityid=1234567890123456789" \\
  -d "tempid=1234567890123456"`}</pre>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* JSON API Section */}
          <div className="bg-white rounded-xl overflow-hidden" style={cardStyle}>
            <div className="px-5 py-3.5 flex items-center gap-2" style={{ background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.06), rgba(6, 182, 212, 0.04))', borderBottom: '1px solid rgba(79, 70, 229, 0.08)' }}>
              <Icons.FileJson className="w-4 h-4 text-indigo-600" />
              <h3 className="text-sm font-bold text-gray-800">JSON API — Send Bulk SMS</h3>
              <span className="ml-auto px-2 py-0.5 rounded-lg text-[10px] font-bold text-white" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>POST</span>
            </div>
            <div className="p-5 space-y-4">
              {/* Endpoint */}
              <div>
                <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Endpoint</h4>
                <div className="flex items-center gap-2 rounded-xl p-3" style={{ background: '#1e1e2e', border: '1px solid rgba(99, 102, 241, 0.15)' }}>
                  <code className="text-xs font-mono text-emerald-400 flex-1 break-all">{apiBaseUrl}/api/json/sendsms/</code>
                  <CopyBtn text={`${apiBaseUrl}/api/json/sendsms/`} label="jsonurl" />
                </div>
              </div>

              {/* Headers */}
              <div>
                <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Request Headers</h4>
                <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(99, 102, 241, 0.08)' }}>
                  <table className="min-w-full">
                    <thead><tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Header</th>
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Value</th>
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Required</th>
                    </tr></thead>
                    <tbody>
                      <tr style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.05)' }}>
                        <td className="px-4 py-2.5 text-xs font-semibold text-indigo-600 font-mono">key</td>
                        <td className="px-4 py-2.5 text-xs text-gray-600 font-mono">{maskedKey}</td>
                        <td className="px-4 py-2.5"><span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-50 text-red-600 border border-red-100">Required</span></td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2.5 text-xs font-semibold text-indigo-600 font-mono">Content-Type</td>
                        <td className="px-4 py-2.5 text-xs text-gray-600 font-mono">application/json</td>
                        <td className="px-4 py-2.5"><span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-50 text-red-600 border border-red-100">Required</span></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Body Fields */}
              <div>
                <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Body Fields (per SMS object in <code className="text-indigo-500 font-mono">listsms</code> array)</h4>
                <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(99, 102, 241, 0.08)' }}>
                  <table className="min-w-full">
                    <thead><tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Field</th>
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Required</th>
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Description</th>
                    </tr></thead>
                    <tbody>
                      {jsonFields.map((f) => (
                        <tr key={f.name} style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.05)' }}>
                          <td className="px-4 py-2.5 text-xs font-semibold text-indigo-600 font-mono">{f.name}</td>
                          <td className="px-4 py-2.5"><span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: 'rgba(99, 102, 241, 0.08)', color: '#4f46e5' }}>{f.type}</span></td>
                          <td className="px-4 py-2.5"><span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-50 text-red-600 border border-red-100">Yes</span></td>
                          <td className="px-4 py-2.5 text-xs text-gray-600">{f.desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Request Body Example */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Request Body Example</h4>
                  <CopyBtn text={jsonExampleBody} label="jsonbody" />
                </div>
                <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(99, 102, 241, 0.15)' }}>
                  <div className="flex items-center px-4 py-2" style={{ background: '#252535' }}>
                    <span className="text-[10px] font-mono text-gray-400">application/json</span>
                  </div>
                  <div className="p-4 overflow-x-auto" style={{ background: '#1e1e2e' }}>
                    <pre className="text-xs font-mono text-emerald-400 whitespace-pre">{jsonExampleBody}</pre>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Success Response */}
          <div className="bg-white rounded-xl overflow-hidden" style={cardStyle}>
            <div className="px-5 py-3.5 flex items-center gap-2" style={{ background: 'rgba(16, 185, 129, 0.04)', borderBottom: '1px solid rgba(16, 185, 129, 0.08)' }}>
              <Icons.CheckCircle className="w-4 h-4 text-emerald-500" />
              <h3 className="text-sm font-bold text-gray-800">Success Response</h3>
              <span className="ml-auto px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-50 text-green-600 border border-green-100">200 OK</span>
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Response Body</span>
                <CopyBtn text={successResponse} label="resp" />
              </div>
              <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(16, 185, 129, 0.15)' }}>
                <div className="p-4 overflow-x-auto" style={{ background: '#1e1e2e' }}>
                  <pre className="text-xs font-mono text-cyan-400 whitespace-pre">{successResponse}</pre>
                </div>
              </div>
            </div>
          </div>

          {/* Response Codes */}
          <div className="bg-white rounded-xl overflow-hidden" style={cardStyle}>
            <div className="px-5 py-3.5 flex items-center gap-2" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
              <Icons.Hash className="w-4 h-4 text-indigo-500" />
              <h3 className="text-sm font-bold text-gray-800">Response Codes</h3>
            </div>
            <div className="p-5">
              <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(99, 102, 241, 0.08)' }}>
                <table className="min-w-full">
                  <thead><tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
                    <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Code</th>
                    <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Description</th>
                  </tr></thead>
                  <tbody>
                    {[
                      { code: '000', status: 'Success', desc: 'Message accepted for delivery', color: '#059669' },
                      { code: '001', status: 'Invalid Key', desc: 'API key is missing or invalid', color: '#dc2626' },
                      { code: '002', status: 'Invalid Mobile', desc: 'Mobile number format is incorrect', color: '#dc2626' },
                      { code: '003', status: 'Insufficient Balance', desc: 'Account balance too low to send', color: '#d97706' },
                      { code: '004', status: 'DLT Validation Failed', desc: 'Entity ID or Template ID not registered', color: '#dc2626' },
                      { code: '005', status: 'Rate Limited', desc: 'TPS limit exceeded, retry after cooldown', color: '#d97706' },
                    ].map((r) => (
                      <tr key={r.code} style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.05)' }}>
                        <td className="px-4 py-2.5"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: `${r.color}15`, color: r.color, border: `1px solid ${r.color}30` }}>{r.code}</span></td>
                        <td className="px-4 py-2.5 text-xs font-semibold text-gray-800">{r.status}</td>
                        <td className="px-4 py-2.5 text-xs text-gray-600">{r.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Coming Soon for other services */}
      {(activeService === 'whatsapp' || activeService === 'rcs' || activeService === 'email') && (
        <div className="bg-white rounded-xl p-10 text-center" style={cardStyle}>
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{
            background: serviceColors[activeService]?.bg,
            color: serviceColors[activeService]?.text,
          }}>
            {(() => { const Icon = Icons[serviceColors[activeService]?.icon]; return Icon ? <Icon className="w-8 h-8" /> : null; })()}
          </div>
          <h3 className="text-lg font-bold text-gray-800 mb-2">{serviceColors[activeService]?.label} API</h3>
          <p className="text-sm text-gray-500 mb-4">API documentation for {serviceColors[activeService]?.label} is coming soon.</p>
          <span className="px-4 py-1.5 rounded-full text-xs font-bold" style={{
            background: serviceColors[activeService]?.bg,
            color: serviceColors[activeService]?.text,
            border: `1px solid ${serviceColors[activeService]?.text}20`,
          }}>Coming Soon</span>
        </div>
      )}
    </div>
  );
};

export default GlobalAPIManagement;
